import React, { FC, FormEvent, useRef, useState, useEffect } from 'react'
import PageContainer from 'components/PageContainer'
import { useUser } from 'common/services/useUser'
import InputGroup from 'components/base/forms/InputGroup'
import { Utils } from 'common/utils'
import Button from 'components/base/forms/Button'
import { useCreateHelpMutation } from 'common/services/useHelp'
import { useVerifyRecaptchaMutation } from 'common/services/useRecaptcha'
import { toast } from 'components/Toast'
import ReCAPTCHA from 'react-google-recaptcha'
import { Project } from 'common/project'
import { getApi } from 'common/api'

type ContactUsPageType = {}

const ContactUsPage: FC<ContactUsPageType> = ({}) => {
  const { data } = useUser({})
  const [email, setEmail] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false)
  const [isVerifyingRecaptcha, setIsVerifyingRecaptcha] = useState(false)
  const [recaptchaReady, setRecaptchaReady] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const isWeb = !getApi().isMobile()
  const requiresCaptcha = !!(isWeb && Project.grecaptcher)

  // Ensure proper cleanup and initialization of reCAPTCHA
  useEffect(() => {
    if (requiresCaptcha) {
      // Add a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setRecaptchaReady(true)
      }, 100)

      return () => {
        clearTimeout(timer)
        // Clean up reCAPTCHA when component unmounts
        try {
          if (recaptchaRef.current) {
            recaptchaRef.current.reset()
          }
        } catch (error) {
          console.error('Error cleaning up reCAPTCHA:', error)
        }
        setRecaptchaReady(false)
        setRecaptchaToken(null)
        setIsRecaptchaVerified(false)
      }
    }
  }, [requiresCaptcha])

  console.log(requiresCaptcha, 'requiresCaptcha')

  const isValid =
    Utils.isValidEmail(email) &&
    !!title &&
    !!description &&
    !!firstName &&
    !!lastName &&
    (!requiresCaptcha || (!!recaptchaToken && isRecaptchaVerified))
  const [sendHelp, { error, isLoading }] = useCreateHelpMutation({})
  const [verifyRecaptchaMutation, { isLoading: isVerifyingMutation }] =
    useVerifyRecaptchaMutation()

  const verifyRecaptcha = async (token: string) => {
    setIsVerifyingRecaptcha(true)
    setIsRecaptchaVerified(false)

    const mutationPromise = verifyRecaptchaMutation({
      recaptcha_token: token,
    })

    mutationPromise
      .then((res: any) => {
        // Check if there's an error first
        if (res?.error) {
          setIsRecaptchaVerified(false)
          // Reset reCAPTCHA on failure
          setRecaptchaToken(null)
          recaptchaRef.current?.reset()
          // @ts-ignore
          toast(
            res.error?.data?.message ||
              'reCAPTCHA verification failed. Please try again.',
          )
        }
        // Check for success - API returns {"message": "reCAPTCHA verified successfully"}
        // or could have success: true field
        else if (
          res?.data &&
          (res.data.success || res.data.message?.includes('successfully'))
        ) {
          setIsRecaptchaVerified(true)
          // Don't reset on success - keep the token
        }
        // If we have data but no clear success indicator, treat as success if no error
        else if (res?.data && !res.error) {
          setIsRecaptchaVerified(true)
        } else {
          setIsRecaptchaVerified(false)
          // Reset reCAPTCHA on failure
          setRecaptchaToken(null)
          recaptchaRef.current?.reset()
          // @ts-ignore
          toast('reCAPTCHA verification failed. Please try again.')
        }
      })
      .catch((error: any) => {
        setIsRecaptchaVerified(false)
        // Reset reCAPTCHA on error
        setRecaptchaToken(null)
        recaptchaRef.current?.reset()
        // @ts-ignore
        toast(
          error?.data?.message ||
            'Failed to verify reCAPTCHA. Please try again.',
        )
      })
      .finally(() => {
        setIsVerifyingRecaptcha(false)
      })
  }

  const handleRecaptchaChange = async (token: string | null) => {
    setRecaptchaToken(token)
    setIsRecaptchaVerified(false)

    if (token && requiresCaptcha) {
      await verifyRecaptcha(token)
    }
  }

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null)
    setIsRecaptchaVerified(false)
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isValid) {
      sendHelp({
        email,
        first_name: firstName,
        last_name: lastName,
        text: description,
        title,
      }).then((res: any) => {
        if (res && !res.error) {
          setEmail('')
          setTitle('')
          setDescription('')
          setFirstName('')
          setLastName('')
          setRecaptchaToken(null)
          setIsRecaptchaVerified(false)
          recaptchaRef.current?.reset()
          // @ts-ignore
          toast('Your message has been sent')
        }
      })
    }
  }
  return (
    <PageContainer>
      <form onSubmit={onSubmit} className='mt-3 container'>
        <h3>Need some help?</h3>
        <p>
          Please provide some details and we’ll get back to you as soon as we
          can!
        </p>
        <div className='col-md-6 mb-3'>
          <InputGroup
            title='Email Address'
            className='mb-2'
            type='email'
            required
            onChange={(e) => {
              setEmail(Utils.safeParseEventValue(e))
            }}
            value={email}
          />
        </div>
        <div className='col-md-6 mb-3'>
          <InputGroup
            title='First Name'
            className='mb-2'
            required
            onChange={(e) => {
              setFirstName(Utils.safeParseEventValue(e))
            }}
            value={firstName}
          />
        </div>
        <div className='col-md-6 mb-3'>
          <InputGroup
            title='Last Name'
            className='mb-2'
            required
            onChange={(e) => {
              setLastName(Utils.safeParseEventValue(e))
            }}
            value={lastName}
          />
        </div>
        <div className='col-md-6 mb-3'>
          <InputGroup
            title='Title'
            className='mb-2'
            required
            onChange={(e) => {
              setTitle(Utils.safeParseEventValue(e))
            }}
            value={title}
          />
        </div>
        <div className='col-md-6 mb-3'>
          <InputGroup
            title='Description'
            required
            className='mb-2'
            textarea
            onChange={(e) => {
              setDescription(Utils.safeParseEventValue(e))
            }}
            value={description}
          />
        </div>
        {requiresCaptcha && (
          <div className='col-md-6 mb-4'>
            <div
              style={{
                clear: 'both',
                marginBottom: '1.5rem',
                marginTop: '1.5rem',
                minHeight: '78px',
                overflow: 'hidden', // Reserve space for reCAPTCHA to prevent layout shift
              }}
            >
              {recaptchaReady && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={Project.grecaptcher}
                  onChange={handleRecaptchaChange}
                  onExpired={handleRecaptchaExpired}
                  onErrored={() => {
                    // Handle reCAPTCHA errors
                    setRecaptchaToken(null)
                    setIsRecaptchaVerified(false)
                    // @ts-ignore
                    toast(
                      'reCAPTCHA error. Please refresh the page and try again.',
                    )
                  }}
                  size='normal'
                />
              )}
              {isVerifyingRecaptcha && (
                <div
                  style={{
                    color: '#666',
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                  }}
                >
                  Verifying reCAPTCHA...
                </div>
              )}
              {recaptchaToken && !isVerifyingRecaptcha && !isRecaptchaVerified && (
                <div
                  style={{
                    color: '#dc3545',
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                  }}
                >
                  reCAPTCHA verification failed. Please try again.
                </div>
              )}
              {isRecaptchaVerified && (
                <div
                  style={{
                    color: '#28a745',
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                  }}
                >
                  ✓ reCAPTCHA verified
                </div>
              )}
            </div>
          </div>
        )}
        <div className='col-md-6 mb-3 d-flex justify-content-end'>
          <div>
            <Button
              type='submit'
              disabled={
                !isValid ||
                isLoading ||
                (requiresCaptcha &&
                  (isVerifyingRecaptcha || isVerifyingMutation))
              }
            >
              {isVerifyingRecaptcha || isVerifyingMutation
                ? 'Verifying...'
                : 'Submit'}
            </Button>
          </div>
        </div>
      </form>
    </PageContainer>
  )
}

export default ContactUsPage
